import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PUBLIC_DOMAIN } from '../../lib/publicDomain';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { api } from '../../lib/api';

// Inline admin tile — compact, white-background card for the platform-admin
// landing. Distinct from the larger student-facing StudentTile.
function AdminTile({ title, description, icon, eyebrow, iconVariant = 'blue', onClick }) {
  return (
    <button type="button" className="admin-tile" onClick={onClick}>
      <div className="admin-tile__header">
        <span className={`admin-tile__icon admin-tile__icon--${iconVariant}`} aria-hidden="true">
          <i className={icon} />
        </span>
        {eyebrow ? <span className="admin-tile__eyebrow">{eyebrow}</span> : null}
      </div>
      <h3 className="admin-tile__title">{title}</h3>
      <p className="admin-tile__copy">{description}</p>
    </button>
  );
}

const TENANT_TYPES = [
  { value: 'university', label: 'University (invoiced)' },
  { value: 'central_students', label: 'Central students (Stripe)' },
];

// ============================================================
// Performance stats sub-view (rendered when view === 'stats')
// ============================================================

function defaultStatsRange() {
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    from: thirtyDaysAgo.toISOString().slice(0, 10),
    to: today.toISOString().slice(0, 10),
  };
}

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };
  const lines = [
    headers.join(','),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function PerformanceStatsView({ token, onBack, renderHeader }) {
  const [tab, setTab] = useState('cases');
  const [range, setRange] = useState(defaultStatsRange);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cases, setCases] = useState([]);
  const [students, setStudents] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [drillCase, setDrillCase] = useState(null); // { caseStudyId, title, sessions }

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'cases') {
        const result = await api.getAdminStatsCases(token, range);
        setCases(result.cases || []);
      } else if (tab === 'students') {
        const result = await api.getAdminStatsStudents(token, range);
        setStudents(result.students || []);
      } else if (tab === 'authors') {
        const result = await api.getAdminStatsAuthors(token, range);
        setAuthors(result.authors || []);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [tab, range, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const totals = useMemo(() => {
    if (tab === 'cases') {
      return {
        sessions: cases.reduce((acc, c) => acc + (c.sessionCount || 0), 0),
        completions: cases.reduce((acc, c) => acc + (c.completionCount || 0), 0),
        rows: cases.length,
        rowsLabel: 'cases',
      };
    }
    if (tab === 'students') {
      return {
        sessions: students.reduce((acc, s) => acc + (s.sessionCount || 0), 0),
        completions: students.reduce((acc, s) => acc + (s.completionCount || 0), 0),
        rows: students.length,
        rowsLabel: 'students',
      };
    }
    return {
      sessions: authors.reduce((acc, a) => acc + (a.sessionCount || 0), 0),
      completions: authors.reduce((acc, a) => acc + (a.completionCount || 0), 0),
      rows: authors.length,
      rowsLabel: 'authors',
    };
  }, [tab, cases, students, authors]);

  const handleExportAuthors = () => {
    const rows = authors.map((a) => ({
      author_email: a.email,
      author_name: a.displayName,
      tenant: a.tenantSlug || '',
      role: a.role,
      cases_owned: a.casesOwned,
      sessions_in_period: a.sessionCount,
      completions_in_period: a.completionCount,
      last_published_at: a.lastPublishedAt || '',
    }));
    downloadCsv(`authors-${range.from}-to-${range.to}.csv`, rows);
  };

  const openDrill = async (caseRow) => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getAdminStatsCaseSessions(caseRow.id, token, range);
      setDrillCase({ caseStudyId: caseRow.id, title: caseRow.title, sessions: result.sessions || [] });
    } catch (err) {
      setError(err?.message || 'Failed to load drill-down');
    } finally {
      setLoading(false);
    }
  };

  if (drillCase) {
    return (
      <div className="student-page">
        <div className="student-page__content">
          {renderHeader(`Sessions — ${drillCase.title}`, `${drillCase.sessions.length} session${drillCase.sessions.length === 1 ? '' : 's'} in period`, {
            label: 'Back to Cases',
            onClick: () => setDrillCase(null),
          })}
          <div className="app-panel p-4">
            {drillCase.sessions.length === 0 ? (
              <p className="text-muted mb-0">No sessions for this case in the selected period.</p>
            ) : (
              <Table responsive hover size="sm" className="facilitator-admin-table mb-0">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Started</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {drillCase.sessions.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div>{s.userDisplayName || s.userEmail || s.userId}</div>
                        {s.userEmail ? <div className="small text-muted">{s.userEmail}</div> : null}
                      </td>
                      <td>
                        {s.status === 'completed' ? <Badge bg="success">Completed</Badge>
                          : s.status === 'in_progress' ? <Badge bg="info">In progress</Badge>
                          : <Badge bg="secondary">{s.status}</Badge>}
                      </td>
                      <td>{s.score !== null ? `${s.score}%` : '—'}</td>
                      <td className="small text-muted">{formatDate(s.startedAt)}</td>
                      <td className="small text-muted">{s.completedAt ? formatDate(s.completedAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="student-page">
      <div className="student-page__content">
        {renderHeader('Performance', 'Engagement stats and per-author rollups for manual payout decisions', {
          label: 'Back to admin home',
          onClick: onBack,
        })}

        <div className="app-panel p-3 mb-3 d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex gap-2">
            {['cases', 'students', 'authors'].map((t) => (
              <Button
                key={t}
                size="sm"
                variant={tab === t ? 'primary' : 'outline-secondary'}
                onClick={() => setTab(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </Button>
            ))}
          </div>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Form.Label className="mb-0 small text-muted">From</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={range.from}
              onChange={(e) => setRange({ ...range, from: e.target.value })}
              style={{ width: 150 }}
            />
            <Form.Label className="mb-0 small text-muted">To</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={range.to}
              onChange={(e) => setRange({ ...range, to: e.target.value })}
              style={{ width: 150 }}
            />
            {tab === 'authors' ? (
              <Button size="sm" variant="success" onClick={handleExportAuthors} disabled={!authors.length}>
                <i className="bi bi-download me-1" /> CSV
              </Button>
            ) : null}
          </div>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        <div className="app-panel p-3 mb-3">
          <div className="text-muted small">
            <strong>{totals.sessions}</strong> sessions, <strong>{totals.completions}</strong> completions across <strong>{totals.rows}</strong> {totals.rowsLabel} in this period.
          </div>
        </div>

        <div className="app-panel p-4">
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
          ) : tab === 'cases' ? (
            cases.length === 0 ? (
              <p className="text-muted mb-0">No cases with activity in this period.</p>
            ) : (
              <Table responsive hover size="sm" className="facilitator-admin-table mb-0">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Tenant</th>
                    <th>Owner</th>
                    <th>Access</th>
                    <th>Tier</th>
                    <th className="text-end">Sessions</th>
                    <th className="text-end">Paid</th>
                    <th className="text-end">Free</th>
                    <th className="text-end">Completed</th>
                    <th className="text-end">Avg score</th>
                    <th>Last activity</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((c) => (
                    <tr key={c.id}>
                      <td><strong>{c.title}</strong></td>
                      <td><code>{c.tenantSlug || c.tenantId}</code></td>
                      <td>
                        <div>{c.ownerDisplayName || c.ownerEmail || '—'}</div>
                        {c.ownerEmail ? <div className="small text-muted">{c.ownerEmail}</div> : null}
                      </td>
                      <td>
                        {c.status === 'pending_approval'
                          ? <Badge bg="info">Pending</Badge>
                          : c.studentAccessEnabled
                            ? <Badge bg="success">On</Badge>
                            : <Badge bg="light" text="dark">Off</Badge>}
                      </td>
                      <td>
                        {c.tenantType === 'central_students'
                          ? (c.freeTierEligible
                              ? <Badge bg="success">Free</Badge>
                              : <Badge bg="secondary">Paid</Badge>)
                          : <span className="small text-muted">N/A</span>}
                      </td>
                      <td className="text-end">{c.sessionCount}</td>
                      <td className="text-end">{c.paidSessionCount || 0}</td>
                      <td className="text-end">{c.freeSessionCount || 0}</td>
                      <td className="text-end">{c.completionCount}</td>
                      <td className="text-end">{c.avgScore !== null ? `${c.avgScore}%` : '—'}</td>
                      <td className="small text-muted">{c.lastActivityAt ? formatDate(c.lastActivityAt) : '—'}</td>
                      <td>
                        <Button size="sm" variant="outline-primary" onClick={() => openDrill(c)}>View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
          ) : tab === 'students' ? (
            students.length === 0 ? (
              <p className="text-muted mb-0">No students in the central tenant yet.</p>
            ) : (
              <Table responsive hover size="sm" className="facilitator-admin-table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Subscription</th>
                    <th className="text-end">Sessions</th>
                    <th className="text-end">Completed</th>
                    <th className="text-end">Avg score</th>
                    <th>Last login</th>
                    <th>Last accessed</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id}>
                      <td>{s.displayName}</td>
                      <td className="text-muted">{s.email}</td>
                      <td><Badge bg="light" text="dark">{s.role}</Badge></td>
                      <td>
                        {s.subscriptionStatus === 'active' || s.subscriptionStatus === 'trialing'
                          ? <Badge bg="success">Paying</Badge>
                          : <Badge bg="light" text="dark">{s.subscriptionStatus}</Badge>}
                      </td>
                      <td className="text-end">{s.sessionCount}</td>
                      <td className="text-end">{s.completionCount}</td>
                      <td className="text-end">{s.avgScore !== null ? `${s.avgScore}%` : '—'}</td>
                      <td className="small text-muted">{s.lastLoginAt ? formatDate(s.lastLoginAt) : '—'}</td>
                      <td className="small text-muted">{s.lastActivityAt ? formatDate(s.lastActivityAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
          ) : (
            authors.length === 0 ? (
              <p className="text-muted mb-0">No authors with published cases yet.</p>
            ) : (
              <Table responsive hover size="sm" className="facilitator-admin-table mb-0">
                <thead>
                  <tr>
                    <th>Author</th>
                    <th>Tenant</th>
                    <th className="text-end">Cases</th>
                    <th className="text-end">Sessions</th>
                    <th className="text-end">Completed</th>
                    <th>Last published</th>
                  </tr>
                </thead>
                <tbody>
                  {authors.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <div>{a.displayName}</div>
                        <div className="small text-muted">{a.email}</div>
                      </td>
                      <td><code>{a.tenantSlug || a.tenantId}</code></td>
                      <td className="text-end">{a.casesOwned}</td>
                      <td className="text-end">{a.sessionCount}</td>
                      <td className="text-end"><strong>{a.completionCount}</strong></td>
                      <td className="small text-muted">{a.lastPublishedAt ? formatDate(a.lastPublishedAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('en-GB');
  } catch (_err) {
    return '';
  }
}

// ============================================================
// Demo-request lead-triage sub-view (rendered when view === 'leads')
// ============================================================

const DEMO_STATUSES = [
  { value: 'new', label: 'New', variant: 'info' },
  { value: 'contacted', label: 'Contacted', variant: 'primary' },
  { value: 'qualified', label: 'Qualified', variant: 'success' },
  { value: 'dead', label: 'Dead', variant: 'secondary' },
];

const DEMO_STATUS_BY_VALUE = Object.fromEntries(DEMO_STATUSES.map((s) => [s.value, s]));

// ============================================================
// Landing-page analytics sub-view (rendered when view === 'traffic')
// Cookieless visit + click counts for www.medi-case.co.uk.
// ============================================================

const LANDING_RANGES = [7, 30, 90, 365];

// Friendly labels for the data-track names on the landing page buttons.
const LANDING_EVENT_LABELS = {
  cta_student_signup: 'Start free (hero)',
  cta_student_signup_card: 'Start free (students card)',
  cta_student_signup_final: 'Start free (bottom of page)',
  cta_uni_demo: 'Book a demo (hero)',
  cta_uni_demo_card: 'Book a demo (universities card)',
  demo_request_submitted: 'Demo form submitted',
};

function LandingBreakdown({ title, rows, emptyText }) {
  return (
    <div className="app-panel p-3 h-100">
      <h3 className="h6 mb-3">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-muted small mb-0">{emptyText}</p>
      ) : (
        <Table size="sm" className="mb-0">
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td className="text-end fw-semibold">{row.n}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function LandingAnalyticsView({ token, onBack, renderHeader }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await api.getAdminLandingAnalytics(token, { days }));
    } catch (err) {
      setError(err?.message || 'Failed to load landing analytics');
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const maxDaily = data ? Math.max(1, ...data.daily.map((d) => d.visitors)) : 1;
  const eventRows = (data?.events || []).map((e) => ({
    label: LANDING_EVENT_LABELS[e.label] || e.label,
    n: e.n,
  }));

  return (
    <div className="student-page">
      <div className="student-page__content">
        {renderHeader('Website traffic', `Visits and button clicks on www.${PUBLIC_DOMAIN}`, {
          label: 'Back to admin home',
          onClick: onBack,
        })}

        <div className="app-panel p-3 mb-3 d-flex flex-wrap align-items-center gap-2">
          {LANDING_RANGES.map((n) => (
            <Button
              key={n}
              size="sm"
              variant={days === n ? 'primary' : 'outline-secondary'}
              onClick={() => setDays(n)}
            >
              Last {n} days
            </Button>
          ))}
          <Button size="sm" variant="outline-secondary" className="ms-auto" onClick={refresh} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1" /> Refresh
          </Button>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {loading && !data ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
        ) : data ? (
          <>
            <Row className="g-3 mb-3">
              <Col sm={6}>
                <div className="app-panel p-3">
                  <div className="small text-muted">Visitors</div>
                  <div className="fs-3 fw-semibold">{data.visitors}</div>
                </div>
              </Col>
              <Col sm={6}>
                <div className="app-panel p-3">
                  <div className="small text-muted">Page views</div>
                  <div className="fs-3 fw-semibold">{data.pageviews}</div>
                </div>
              </Col>
            </Row>

            <div className="app-panel p-3 mb-3">
              <h3 className="h6 mb-3">Visitors per day</h3>
              {data.daily.length === 0 ? (
                <p className="text-muted small mb-0">No visits recorded in this period yet.</p>
              ) : (
                <Table size="sm" className="mb-0">
                  <tbody>
                    {data.daily.map((d) => (
                      <tr key={d.day}>
                        <td className="text-muted small" style={{ width: 110 }}>{d.day}</td>
                        <td>
                          <div
                            style={{
                              width: `${(d.visitors / maxDaily) * 100}%`,
                              minWidth: 4,
                              height: 12,
                              borderRadius: 3,
                              background: 'var(--bs-primary)',
                            }}
                            aria-hidden="true"
                          />
                        </td>
                        <td className="text-end fw-semibold" style={{ width: 60 }}>{d.visitors}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            <Row className="g-3">
              <Col md={6}>
                <LandingBreakdown title="Button clicks" rows={eventRows} emptyText="No button clicks yet." />
              </Col>
              <Col md={6}>
                <LandingBreakdown
                  title="Where visitors came from"
                  rows={data.referrers}
                  emptyText="No visits recorded in this period yet."
                />
              </Col>
              <Col md={6}>
                <LandingBreakdown
                  title="Campaigns (utm_source / medium / campaign)"
                  rows={data.campaigns}
                  emptyText="No campaign links used yet. Add ?utm_source=... to links you share."
                />
              </Col>
              <Col md={6}>
                <LandingBreakdown title="Devices" rows={data.devices} emptyText="No visits recorded yet." />
              </Col>
            </Row>
          </>
        ) : null}
      </div>
    </div>
  );
}

function DemoRequestsView({ token, onBack, renderHeader, onCountsChange }) {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ new: 0, contacted: 0, qualified: 0, dead: 0 });
  const [statusFilter, setStatusFilter] = useState(''); // '' = all
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState('');
  // Per-row draft state so notes + status edits don't fight the loaded data
  // until the user saves. Keyed by request id.
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.listAdminDemoRequests(token, statusFilter ? { status: statusFilter } : {});
      setRequests(result.requests || []);
      setCounts(result.counts || { new: 0, contacted: 0, qualified: 0, dead: 0 });
      if (typeof onCountsChange === 'function') onCountsChange(result.counts || {});
    } catch (err) {
      setError(err?.message || 'Failed to load demo requests');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, onCountsChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getDraft = (request) => drafts[request.id] || { status: request.status, notes: request.notes };
  const setDraftField = (id, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  const handleSave = async (request) => {
    const draft = getDraft(request);
    setSavingId(request.id);
    setError('');
    try {
      const result = await api.updateAdminDemoRequest(
        request.id,
        { status: draft.status, notes: draft.notes },
        token
      );
      // Merge the server-returned row back, clear local draft
      setRequests((prev) => prev.map((r) => (r.id === request.id ? result.request : r)));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[request.id];
        return next;
      });
      // Refresh counts since a status change moves the row across buckets
      refresh();
    } catch (err) {
      setError(err?.message || 'Failed to save changes');
    } finally {
      setSavingId('');
    }
  };

  return (
    <div className="student-page">
      <div className="student-page__content">
        {renderHeader('Demo requests', 'University-side leads captured by the public landing page', {
          label: 'Back to admin home',
          onClick: onBack,
        })}

        <div className="app-panel p-3 mb-3 d-flex flex-wrap align-items-center gap-2">
          <Button
            size="sm"
            variant={statusFilter === '' ? 'primary' : 'outline-secondary'}
            onClick={() => setStatusFilter('')}
          >
            All <Badge bg="light" text="dark" className="ms-1">{requests.length}</Badge>
          </Button>
          {DEMO_STATUSES.map((s) => (
            <Button
              key={s.value}
              size="sm"
              variant={statusFilter === s.value ? 'primary' : 'outline-secondary'}
              onClick={() => setStatusFilter(s.value)}
            >
              {s.label} <Badge bg="light" text="dark" className="ms-1">{counts[s.value] || 0}</Badge>
            </Button>
          ))}
          <Button size="sm" variant="outline-secondary" className="ms-auto" onClick={refresh} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1" /> Refresh
          </Button>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        <div className="app-panel p-0">
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
          ) : requests.length === 0 ? (
            <p className="text-muted mb-0 p-4">
              No demo requests in this view. The form at <code>www.{PUBLIC_DOMAIN}/#book-a-demo</code> creates rows here.
            </p>
          ) : (
            <Table responsive hover size="sm" className="facilitator-admin-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Received</th>
                  <th>Contact</th>
                  <th>Institution</th>
                  <th>Role</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const statusDef = DEMO_STATUS_BY_VALUE[r.status] || DEMO_STATUSES[0];
                  const isOpen = expandedId === r.id;
                  const draft = getDraft(r);
                  const dirty = draft.status !== r.status || draft.notes !== r.notes;
                  return (
                    <React.Fragment key={r.id}>
                      <tr onClick={() => setExpandedId(isOpen ? '' : r.id)} style={{ cursor: 'pointer' }}>
                        <td className="text-muted small">{formatDate(r.createdAt)}</td>
                        <td>
                          <div><strong>{r.name}</strong></div>
                          <div className="small">
                            <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()}>{r.email}</a>
                          </div>
                        </td>
                        <td>{r.institution || <span className="text-muted">—</span>}</td>
                        <td className="small">{r.role || <span className="text-muted">—</span>}</td>
                        <td><Badge bg={statusDef.variant}>{statusDef.label}</Badge></td>
                        <td className="text-end">
                          <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr>
                          <td colSpan={6} style={{ background: 'var(--app-bg-soft, #f8f9fa)' }}>
                            <div className="p-3">
                              <div className="mb-3">
                                <div className="small text-muted mb-1">Their message</div>
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                  {r.message || <span className="text-muted">(none provided)</span>}
                                </div>
                              </div>
                              <Row className="g-3">
                                <Col md={4}>
                                  <Form.Group>
                                    <Form.Label className="small text-muted mb-1">Status</Form.Label>
                                    <Form.Select
                                      size="sm"
                                      value={draft.status}
                                      onChange={(e) => setDraftField(r.id, 'status', e.target.value)}
                                    >
                                      {DEMO_STATUSES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                      ))}
                                    </Form.Select>
                                  </Form.Group>
                                </Col>
                                <Col md={8}>
                                  <Form.Group>
                                    <Form.Label className="small text-muted mb-1">Notes (internal)</Form.Label>
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      value={draft.notes}
                                      onChange={(e) => setDraftField(r.id, 'notes', e.target.value)}
                                      placeholder="Follow-up date, qualification notes, next action…"
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>
                              <div className="d-flex gap-2 mt-3">
                                <Button
                                  size="sm"
                                  variant="primary"
                                  disabled={!dirty || savingId === r.id}
                                  onClick={() => handleSave(r)}
                                >
                                  {savingId === r.id ? 'Saving…' : 'Save'}
                                </Button>
                                <a
                                  className="btn btn-sm btn-outline-secondary"
                                  href={`mailto:${r.email}?subject=${encodeURIComponent('Re: your MediCase demo request')}`}
                                >
                                  <i className="bi bi-envelope me-1" /> Reply
                                </a>
                                <span className="text-muted small ms-auto align-self-center">
                                  Request ID <code>{r.id}</code>
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}

const EMPTY_DRAFT = {
  slug: '',
  displayName: '',
  tenantType: 'university',
  contactEmail: '',
};

const EMPTY_EDIT = {
  displayName: '',
  tenantType: 'university',
  status: 'active',
  contactEmail: '',
  brandPrimaryColor: '',
  brandSecondaryColor: '',
  brandLogoUrl: '',
  brandLoginTagline: '',
  accessPaidUntil: '',
  billingNotes: '',
  requireCaseApproval: false,
};

const PlatformAdminWorkspace = ({ token, onBack, onTenantUpdated }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Views: 'home' (tiles) | 'list' (tenant CRUD) | 'edit' | 'users'
  //        'students' (shortcut to central tenant users) | 'promo' (marketing)
  //        | 'stats' (performance) | 'leads' (demo-request triage)
  const [view, setView] = useState('home');
  const [demoLeadCounts, setDemoLeadCounts] = useState({ new: 0, contacted: 0, qualified: 0, dead: 0 });
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [editDraft, setEditDraft] = useState(EMPTY_EDIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [editError, setEditError] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [createDraft, setCreateDraft] = useState(EMPTY_DRAFT);
  const [createError, setCreateError] = useState('');
  const [createSaving, setCreateSaving] = useState(false);

  const refreshTenants = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.listAdminTenants(token);
      setTenants(result.tenants || []);
    } catch (err) {
      setError(err?.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const openDoc = useCallback(async (docPath) => {
    try {
      const res = await fetch(`/api/_admin/docs/${docPath}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        window.alert('Could not open document.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      window.alert('Failed to open document.');
    }
  }, [token]);

  useEffect(() => {
    refreshTenants();
  }, [refreshTenants]);

  // Fetch demo-request counts so the home tile can flag untriaged leads
  // without the user having to drill in. Failure is non-fatal — the tile
  // just won't show a count.
  useEffect(() => {
    let cancelled = false;
    api.listAdminDemoRequests(token)
      .then((result) => {
        if (!cancelled && result?.counts) setDemoLeadCounts(result.counts);
      })
      .catch(() => { /* silent — tile renders without an eyebrow count */ });
    return () => { cancelled = true; };
  }, [token]);

  // SPA history for the inner Platform-admin views (home / list / edit /
  // users / students / promo / stats). The App-level history manager only
  // sees currentView === 'platform-admin' and never knows the user has
  // drilled into a sub-view, so without this every browser Back from any
  // sub-view jumps straight off the SPA. Push a state on every view
  // change and respond to popstate by updating view.
  const isPopNavigation = useRef(false);
  useEffect(() => {
    if (isPopNavigation.current) {
      isPopNavigation.current = false;
      return;
    }
    if (typeof window === 'undefined') return;
    try {
      window.history.pushState({ platformAdminView: view }, '', window.location.pathname + window.location.search);
    } catch (_error) {
      // best-effort
    }
  }, [view]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // Stamp the entry that exists when this component mounts so the very
    // first Back press here returns to 'home' rather than escaping the SPA.
    try {
      const stateNow = window.history.state;
      if (!stateNow?.platformAdminView) {
        window.history.replaceState({ platformAdminView: view }, '', window.location.pathname + window.location.search);
      }
    } catch (_error) {
      // best-effort
    }
    const onPopState = (event) => {
      const next = event.state?.platformAdminView;
      if (next) {
        isPopNavigation.current = true;
        setView(next);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.id === selectedTenantId) || null,
    [tenants, selectedTenantId]
  );

  const openEdit = useCallback((tenant) => {
    setSelectedTenantId(tenant.id);
    setEditDraft({
      displayName: tenant.displayName || '',
      tenantType: tenant.tenantType || 'university',
      status: tenant.status || 'active',
      contactEmail: tenant.contactEmail || '',
      brandPrimaryColor: tenant.brandPrimaryColor || '',
      brandSecondaryColor: tenant.brandSecondaryColor || '',
      brandLogoUrl: tenant.brandLogoUrl || '',
      brandLoginTagline: tenant.brandLoginTagline || '',
      accessPaidUntil: tenant.accessPaidUntil ? new Date(tenant.accessPaidUntil).toISOString().slice(0, 10) : '',
      billingNotes: tenant.billingNotes || '',
      requireCaseApproval: Boolean(tenant.requireCaseApproval),
    });
    setEditMessage('');
    setEditError('');
    setView('edit');
  }, []);

  const openUsers = useCallback(async (tenant) => {
    setSelectedTenantId(tenant.id);
    setView('users');
    setUsersLoading(true);
    try {
      const result = await api.listAdminTenantUsers(tenant.id, token);
      setUsers(result.users || []);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [token]);

  const handleEditSave = async () => {
    setEditSaving(true);
    setEditError('');
    setEditMessage('');
    try {
      const result = await api.updateAdminTenant(selectedTenantId, editDraft, token);
      setEditMessage('Saved.');
      setTenants((prev) => prev.map((t) => (t.id === result.tenant.id ? result.tenant : t)));
      if (onTenantUpdated) onTenantUpdated(result.tenant);
    } catch (err) {
      setEditError(err?.message || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    setCreateSaving(true);
    setCreateError('');
    try {
      await api.createAdminTenant(createDraft, token);
      setCreateModal(false);
      setCreateDraft(EMPTY_DRAFT);
      await refreshTenants();
    } catch (err) {
      setCreateError(err?.message || 'Failed to create tenant');
    } finally {
      setCreateSaving(false);
    }
  };

  const formatErrorAlert = (err, fallback) => {
    const requestId = err?.payload?.requestId;
    const message = err?.message || fallback;
    return requestId
      ? `${message}\n\nRequest ID: ${requestId}\n(Quote this when reporting the error.)`
      : message;
  };

  const handleSuspendUser = async (userId) => {
    const reason = window.prompt('Suspension reason');
    if (!reason) return;
    try {
      await api.suspendAdminTenantUser(selectedTenantId, userId, reason, token);
      const result = await api.listAdminTenantUsers(selectedTenantId, token);
      setUsers(result.users || []);
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to suspend user'));
    }
  };

  const handleRestoreUser = async (userId) => {
    if (!window.confirm('Restore this account?')) return;
    try {
      await api.restoreAdminTenantUser(selectedTenantId, userId, token);
      const result = await api.listAdminTenantUsers(selectedTenantId, token);
      setUsers(result.users || []);
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to restore user'));
    }
  };

  const handleChangeRole = async (userId, nextRole, currentRole) => {
    if (nextRole === currentRole) return;
    try {
      await api.setAdminTenantUserRole(selectedTenantId, userId, nextRole, token);
      // Optimistic local update so the dropdown reflects immediately
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: nextRole } : u)));
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to change role'));
    }
  };

  const [seedingTenantId, setSeedingTenantId] = useState('');
  const handleSeedSampleCase = async (tenantId) => {
    if (!window.confirm('Seed the built-in sample case study into this tenant? Only runs if the tenant has zero cases AND has at least one educator.')) return;
    setSeedingTenantId(tenantId);
    try {
      const result = await api.seedAdminTenantSampleCase(tenantId, token);
      window.alert(`Sample case seeded. Owned by user ${result.ownerUserId}. Refresh to see updated counts.`);
      await refreshTenants();
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to seed sample case'));
    } finally {
      setSeedingTenantId('');
    }
  };

  const [seedingCurriculumTenantId, setSeedingCurriculumTenantId] = useState('');
  const handleSeedCurriculum = async (tenantId) => {
    if (!window.confirm('Seed the curated curriculum library into this tenant? Cases are inserted as drafts (not visible to students). Idempotent on case title so it is safe to press repeatedly.')) return;
    setSeedingCurriculumTenantId(tenantId);
    try {
      const result = await api.seedAdminTenantCurriculum(tenantId, token);
      window.alert(`Curriculum seed complete. Created ${result.created} of ${result.total} (skipped ${result.skipped} that already exist, preserved ${result.preservedPublished || 0} published). All new cases are drafts pending review.`);
      await refreshTenants();
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to seed curriculum library'));
    } finally {
      setSeedingCurriculumTenantId('');
    }
  };

  const handleRefreshCurriculum = async (tenantId) => {
    if (!window.confirm(
      'Refresh curriculum drafts from the latest library?\n\n' +
      'Every UNPUBLISHED case study that came from the seed library will be REPLACED with the latest content. Any case you have published (used in a live class, sent to students, or that has student attempts) will be left untouched.\n\n' +
      'New cases added since you last seeded will also be inserted.\n\n' +
      'Continue?'
    )) return;
    setSeedingCurriculumTenantId(tenantId);
    try {
      const result = await api.seedAdminTenantCurriculum(tenantId, token, { overwrite: true });
      window.alert(
        `Curriculum refresh complete.\n\n` +
        `Created (new cases): ${result.created}\n` +
        `Updated (refreshed drafts): ${result.updated}\n` +
        `Preserved (published, untouched): ${result.preservedPublished}\n` +
        `Library total: ${result.total}`
      );
      await refreshTenants();
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to refresh curriculum library'));
    } finally {
      setSeedingCurriculumTenantId('');
    }
  };

  const [seedingDrillsTenantId, setSeedingDrillsTenantId] = useState('');
  const handleSeedDrills = async (tenantId) => {
    if (!window.confirm('Seed the curated calculation-exercise library into this tenant? Exercises land as drafts; idempotent on title.')) return;
    setSeedingDrillsTenantId(tenantId);
    try {
      const result = await api.seedAdminTenantDrills(tenantId, token);
      window.alert(`Exercise seed complete. Created ${result.created} of ${result.total} (skipped ${result.skipped} that already exist, preserved ${result.preservedPublished || 0} published).`);
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to seed exercise library'));
    } finally {
      setSeedingDrillsTenantId('');
    }
  };

  const handleRefreshDrills = async (tenantId) => {
    if (!window.confirm(
      'Refresh exercise drafts from the latest library?\n\n' +
      'Every UNPUBLISHED exercise that came from the seed library will be REPLACED with the latest content. Published exercises are left untouched.\n\n' +
      'New exercises added since you last seeded will also be inserted.\n\n' +
      'Continue?'
    )) return;
    setSeedingDrillsTenantId(tenantId);
    try {
      const result = await api.seedAdminTenantDrills(tenantId, token, { overwrite: true });
      window.alert(
        `Exercise refresh complete.\n\n` +
        `Created (new exercises): ${result.created}\n` +
        `Updated (refreshed drafts): ${result.updated}\n` +
        `Preserved (published, untouched): ${result.preservedPublished}\n` +
        `Library total: ${result.total}`
      );
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to refresh exercise library'));
    } finally {
      setSeedingDrillsTenantId('');
    }
  };

  const [seedingSpotErrorTenantId, setSeedingSpotErrorTenantId] = useState('');
  const handleSeedSpotError = async (tenantId) => {
    if (!window.confirm('Seed the curated Spot-the-Error exercise library into this tenant? Exercises land as drafts; idempotent on title.')) return;
    setSeedingSpotErrorTenantId(tenantId);
    try {
      const result = await api.seedAdminTenantSpotErrorDrills(tenantId, token);
      window.alert(`Spot-the-Error seed complete. Created ${result.created} of ${result.total} (skipped ${result.skipped} that already exist, preserved ${result.preservedPublished || 0} published).`);
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to seed spot-the-error library'));
    } finally {
      setSeedingSpotErrorTenantId('');
    }
  };

  const handleRefreshSpotError = async (tenantId) => {
    if (!window.confirm(
      'Refresh Spot-the-Error exercise drafts from the latest library?\n\n' +
      'Every UNPUBLISHED exercise that came from the seed library will be REPLACED with the latest content. Published exercises are left untouched.\n\n' +
      'New exercises added since you last seeded will also be inserted.\n\n' +
      'Continue?'
    )) return;
    setSeedingSpotErrorTenantId(tenantId);
    try {
      const result = await api.seedAdminTenantSpotErrorDrills(tenantId, token, { overwrite: true });
      window.alert(
        `Spot-the-Error refresh complete.\n\n` +
        `Created (new exercises): ${result.created}\n` +
        `Updated (refreshed drafts): ${result.updated}\n` +
        `Preserved (published, untouched): ${result.preservedPublished}\n` +
        `Library total: ${result.total}`
      );
    } catch (err) {
      window.alert(formatErrorAlert(err, 'Failed to refresh spot-the-error library'));
    } finally {
      setSeedingSpotErrorTenantId('');
    }
  };

  const renderHeader = (title, subtitle, backOpts = null) => (
    <div className="admin-page-header">
      <div>
        <h1 className="admin-page-header__title">{title}</h1>
        {subtitle ? <p className="admin-page-header__subtitle">{subtitle}</p> : null}
      </div>
      <div className="d-flex gap-2">
        {backOpts ? (
          <Button variant="outline-secondary" size="sm" onClick={() => {
            backOpts.onClick();
            setEditMessage('');
            setEditError('');
          }}>
            {backOpts.label}
          </Button>
        ) : null}
        <Button variant="outline-secondary" size="sm" onClick={onBack}>Exit admin</Button>
      </div>
    </div>
  );

  const centralTenant = useMemo(
    () => tenants.find((t) => t.tenantType === 'central_students') || null,
    [tenants]
  );

  // For the Student accounts tile we count paying subscribers from the
  // most recently loaded users list (only meaningful once the user has
  // visited the Students view at least once). Until then, just show
  // the central user count.
  const payingCount = useMemo(() => {
    if (!users.length) return null;
    return users.filter((u) =>
      u.subscriptionStatus === 'active' || u.subscriptionStatus === 'trialing'
    ).length;
  }, [users]);

  const centralTenantSummary = useMemo(() => {
    if (loading) return 'Loading…';
    if (!centralTenant) return 'No central tenant';
    const total = centralTenant.userCount;
    const totalLabel = `${total} user${total === 1 ? '' : 's'}`;
    if (payingCount === null) return totalLabel;
    return `${totalLabel} • ${payingCount} paying`;
  }, [centralTenant, loading, payingCount]);

  const openStudentAccounts = useCallback(async () => {
    if (!centralTenant) return;
    setSelectedTenantId(centralTenant.id);
    setView('students');
    setUsersLoading(true);
    try {
      const result = await api.listAdminTenantUsers(centralTenant.id, token);
      setUsers(result.users || []);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [centralTenant, token]);

  if (view === 'home') {
    return (
      <div className="student-page">
        <div className="student-page__content">
          {renderHeader('Platform admin', 'Cross-tenant management for the whole platform')}
          {error ? <Alert variant="danger">{error}</Alert> : null}
          <Row className="g-4 mb-4">
            <Col md={6} xl={4}>
              <AdminTile
                title="Tenant management"
                description="Create new universities, edit billing windows, branding, and access status. Manage users in any tenant."
                eyebrow={loading ? 'Loading…' : `${tenants.length} tenant${tenants.length === 1 ? '' : 's'}`}
                icon="bi bi-building"
                iconVariant="blue"
                onClick={() => setView('list')}
              />
            </Col>
            <Col md={6} xl={4}>
              <AdminTile
                title="Student accounts"
                description="Manage self-signup students on the central tenant. Promote a pharmacist to educator so they can author cases."
                eyebrow={centralTenantSummary}
                icon="bi bi-person-badge"
                iconVariant="teal"
                onClick={openStudentAccounts}
              />
            </Col>
            <Col md={6} xl={4}>
              <AdminTile
                title="Promotional materials"
                description="University flyer (PDF), feature walkthrough deck (PPTX), operational playbooks, and business reference."
                eyebrow="Sales + onboarding"
                icon="bi bi-megaphone"
                iconVariant="amber"
                onClick={() => setView('promo')}
              />
            </Col>
            <Col md={6} xl={4}>
              <AdminTile
                title="Performance"
                description="Per-case, per-student, and per-author engagement stats. Use Authors CSV export for manual contributor payouts."
                eyebrow="Stats + payouts"
                icon="bi bi-bar-chart"
                iconVariant="purple"
                onClick={() => setView('stats')}
              />
            </Col>
            <Col md={6} xl={4}>
              <AdminTile
                title="Demo requests"
                description="University-side leads from the public landing page. Triage status, log follow-up notes, and reply by email."
                eyebrow={
                  demoLeadCounts.new > 0
                    ? `${demoLeadCounts.new} new lead${demoLeadCounts.new === 1 ? '' : 's'}`
                    : 'Sales pipeline'
                }
                icon="bi bi-inbox"
                iconVariant="teal"
                onClick={() => setView('leads')}
              />
            </Col>
            <Col md={6} xl={4}>
              <AdminTile
                title="Website traffic"
                description="Visitors, traffic sources and button clicks on the public landing page. Cookieless, stored in our own database."
                eyebrow="Landing analytics"
                icon="bi bi-graph-up"
                iconVariant="blue"
                onClick={() => setView('traffic')}
              />
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  if (view === 'stats') {
    return (
      <PerformanceStatsView
        token={token}
        onBack={() => setView('home')}
        renderHeader={renderHeader}
      />
    );
  }

  if (view === 'traffic') {
    return (
      <LandingAnalyticsView
        token={token}
        onBack={() => setView('home')}
        renderHeader={renderHeader}
      />
    );
  }

  if (view === 'leads') {
    return (
      <DemoRequestsView
        token={token}
        onBack={() => setView('home')}
        renderHeader={renderHeader}
        onCountsChange={setDemoLeadCounts}
      />
    );
  }

  if (view === 'promo') {
    return (
      <div className="student-page">
        <div className="student-page__content">
          {renderHeader('Promotional materials', 'Sales-ready assets and operational playbooks', {
            label: 'Back to admin home',
            onClick: () => setView('home'),
          })}
          <div className="app-panel p-4">
            <h2 className="h5 mb-3">Sales assets</h2>
            <p className="text-muted small">Click to download. The flyer is a single-page A4; the deck is a 13-slide demo for 20-minute calls.</p>
            <ul className="list-unstyled mb-4">
              <li className="mb-3">
                <i className="bi bi-file-earmark-pdf me-2" style={{ color: 'var(--app-primary)' }}></i>
                <a href="/marketing/university-flyer.pdf" target="_blank" rel="noopener noreferrer" download>
                  University flyer (PDF)
                </a>
                <div className="text-muted small ms-4">Send with sales emails. One-page A4, navy + teal brand.</div>
              </li>
              <li className="mb-0">
                <i className="bi bi-easel me-2" style={{ color: 'var(--app-primary)' }}></i>
                <a href="/marketing/feature-walkthrough.pptx" target="_blank" rel="noopener noreferrer" download>
                  Feature walkthrough deck (PPTX)
                </a>
                <div className="text-muted small ms-4">13 slides. Title, problem, solution, six features, security, pricing, roadmap, CTA.</div>
              </li>
            </ul>

            <hr className="my-4" />

            <h2 className="h5 mb-3">Operational playbooks</h2>
            <p className="text-muted small">
              Open in-browser. Rendered from the markdown source on every deploy — no GitHub sign-in needed.
            </p>
            <ul className="list-unstyled mb-0">
              <li className="mb-2">
                <i className="bi bi-clipboard-check me-2" style={{ color: 'var(--app-primary)' }}></i>
                <button type="button" className="btn btn-link p-0" onClick={() => openDoc('onboarding/university-onboarding.html')}>
                  University onboarding playbook
                </button>
                <span className="text-muted ms-2 small">— end-to-end process from contract to live</span>
              </li>
              <li className="mb-2">
                <i className="bi bi-megaphone me-2" style={{ color: 'var(--app-primary)' }}></i>
                <button type="button" className="btn btn-link p-0" onClick={() => openDoc('promotion/student-social-media-plan.html')}>
                  Student social media plan
                </button>
                <span className="text-muted ms-2 small">— acquisition strategy for direct signups</span>
              </li>
              <li className="mb-0">
                <i className="bi bi-book me-2" style={{ color: 'var(--app-primary)' }}></i>
                <button type="button" className="btn btn-link p-0" onClick={() => openDoc('admin-setup-guide.html')}>
                  Admin setup guide
                </button>
                <span className="text-muted ms-2 small">— operational reference for this dashboard</span>
              </li>
            </ul>

            <hr className="my-4" />

            <h2 className="h5 mb-3">Business reference</h2>
            <p className="text-muted small">
              Pricing, business plan, and B2B marketing playbook. Update by editing the markdown
              source in the repo and re-running <code>python marketing/build_docs.py</code>.
            </p>
            <ul className="list-unstyled mb-0">
              <li className="mb-2">
                <i className="bi bi-currency-pound me-2" style={{ color: 'var(--app-primary)' }}></i>
                <button type="button" className="btn btn-link p-0" onClick={() => openDoc('business/university-pricing.html')}>
                  University pricing
                </button>
                <span className="text-muted ms-2 small">— pricing tiers, negotiation guidance, contract terms</span>
              </li>
              <li className="mb-2">
                <i className="bi bi-graph-up-arrow me-2" style={{ color: 'var(--app-primary)' }}></i>
                <button type="button" className="btn btn-link p-0" onClick={() => openDoc('business/year-1-business-plan.html')}>
                  Year 1 business plan
                </button>
                <span className="text-muted ms-2 small">— quarterly targets, costs, time allocation, key risks</span>
              </li>
              <li className="mb-0">
                <i className="bi bi-bullseye me-2" style={{ color: 'var(--app-primary)' }}></i>
                <button type="button" className="btn btn-link p-0" onClick={() => openDoc('business/b2b-marketing-plan.html')}>
                  B2B marketing plan
                </button>
                <span className="text-muted ms-2 small">— target list, channels, outreach templates, metrics</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'edit' && selectedTenant) {
    return (
      <div className="student-page">
        <div className="student-page__content">
          {renderHeader(`Edit tenant — ${selectedTenant.displayName}`, `Slug: ${selectedTenant.slug}`, {
            label: 'Back to tenants',
            onClick: () => setView('list'),
          })}
          <Row className="g-4">
            <Col lg={7}>
              <div className="app-panel p-4">
                <h2 className="h5 mb-3">Identity</h2>
                {editError ? <Alert variant="danger">{editError}</Alert> : null}
                {editMessage ? <Alert variant="success">{editMessage}</Alert> : null}
                <Form.Group className="mb-3">
                  <Form.Label>Display name</Form.Label>
                  <Form.Control
                    value={editDraft.displayName}
                    onChange={(e) => setEditDraft({ ...editDraft, displayName: e.target.value })}
                  />
                </Form.Group>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tenant type</Form.Label>
                      <Form.Select
                        value={editDraft.tenantType}
                        onChange={(e) => setEditDraft({ ...editDraft, tenantType: e.target.value })}
                      >
                        {TENANT_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={editDraft.status}
                        onChange={(e) => setEditDraft({ ...editDraft, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                      </Form.Select>
                      <Form.Text className="text-muted">Suspended tenants reject all requests.</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Contact email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editDraft.contactEmail}
                    onChange={(e) => setEditDraft({ ...editDraft, contactEmail: e.target.value })}
                  />
                </Form.Group>

                <hr className="my-4" />

                <h2 className="h5 mb-3">Branding</h2>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Primary colour</Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control
                          type="color"
                          value={editDraft.brandPrimaryColor || '#0d6efd'}
                          onChange={(e) => setEditDraft({ ...editDraft, brandPrimaryColor: e.target.value })}
                          style={{ width: 64, padding: 4 }}
                        />
                        <Form.Control
                          value={editDraft.brandPrimaryColor}
                          onChange={(e) => setEditDraft({ ...editDraft, brandPrimaryColor: e.target.value })}
                          placeholder="#0d6efd"
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Secondary colour</Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control
                          type="color"
                          value={editDraft.brandSecondaryColor || '#6c757d'}
                          onChange={(e) => setEditDraft({ ...editDraft, brandSecondaryColor: e.target.value })}
                          style={{ width: 64, padding: 4 }}
                        />
                        <Form.Control
                          value={editDraft.brandSecondaryColor}
                          onChange={(e) => setEditDraft({ ...editDraft, brandSecondaryColor: e.target.value })}
                          placeholder="#6c757d"
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Logo URL</Form.Label>
                  <Form.Control
                    value={editDraft.brandLogoUrl}
                    onChange={(e) => setEditDraft({ ...editDraft, brandLogoUrl: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                  <Form.Text className="text-muted">PNG/SVG. Renders to the left of the navbar brand.</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Login tagline</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editDraft.brandLoginTagline}
                    onChange={(e) => setEditDraft({ ...editDraft, brandLoginTagline: e.target.value })}
                    placeholder="Shown above the login form, e.g. University of Oxford prescribing training"
                  />
                </Form.Group>

                <hr className="my-4" />

                <h2 className="h5 mb-3">Billing</h2>
                <p className="text-muted small">
                  Universities are invoiced outside the platform. Set the access window manually here.
                  Central-students tenants charge per-student via Stripe; ignore this section for those.
                </p>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Access paid until</Form.Label>
                      <Form.Control
                        type="date"
                        value={editDraft.accessPaidUntil}
                        onChange={(e) => setEditDraft({ ...editDraft, accessPaidUntil: e.target.value })}
                      />
                      <Form.Text className="text-muted">
                        Past this date, all users in this tenant lose access. Leave blank to never expire.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Billing notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={editDraft.billingNotes}
                    onChange={(e) => setEditDraft({ ...editDraft, billingNotes: e.target.value })}
                    placeholder="PO number, invoice contact, renewal terms (internal use only)."
                  />
                </Form.Group>

                <hr className="my-4" />

                <h2 className="h5 mb-3">Case approval workflow</h2>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="require-case-approval"
                    label="Require facilitator-admin approval before a new case is visible to students"
                    checked={Boolean(editDraft.requireCaseApproval)}
                    onChange={(e) => setEditDraft({ ...editDraft, requireCaseApproval: e.target.checked })}
                  />
                  <Form.Text className="text-muted">
                    When on, cases authored by regular educators go to a pending queue. A facilitator-admin
                    must approve before students can run them. Facilitator-admins always self-approve.
                  </Form.Text>
                </Form.Group>

                <Button variant="primary" onClick={handleEditSave} disabled={editSaving}>
                  {editSaving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </Col>
            <Col lg={5}>
              <div className="app-panel p-4">
                <h2 className="h5 mb-3">Stats</h2>
                <dl className="row mb-0">
                  <dt className="col-6">Tenant ID</dt>
                  <dd className="col-6"><code>{selectedTenant.id}</code></dd>
                  <dt className="col-6">Users</dt>
                  <dd className="col-6">{selectedTenant.userCount}</dd>
                  <dt className="col-6">Case studies</dt>
                  <dd className="col-6">{selectedTenant.caseStudyCount}</dd>
                  <dt className="col-6">Created</dt>
                  <dd className="col-6">{formatDate(selectedTenant.createdAt)}</dd>
                  <dt className="col-6">Updated</dt>
                  <dd className="col-6">{formatDate(selectedTenant.updatedAt)}</dd>
                </dl>
                <hr />
                <div className="d-grid gap-2">
                  <Button variant="outline-primary" onClick={() => openUsers(selectedTenant)}>
                    View users in this tenant
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleSeedSampleCase(selectedTenant.id)}
                    disabled={seedingTenantId === selectedTenant.id || selectedTenant.caseStudyCount > 0}
                    title={selectedTenant.caseStudyCount > 0
                      ? 'Tenant already has case studies'
                      : "Insert Ben's built-in sample case (only works if tenant has zero cases + at least one educator)"}
                  >
                    {seedingTenantId === selectedTenant.id ? 'Seeding…' : 'Seed sample case'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleSeedCurriculum(selectedTenant.id)}
                    disabled={seedingCurriculumTenantId === selectedTenant.id}
                    title="Insert the curated curriculum library (Year 1-4 + postgrad). Cases are inserted as drafts, idempotent on case title so it is safe to re-run."
                  >
                    {seedingCurriculumTenantId === selectedTenant.id ? 'Seeding…' : 'Seed curriculum library (167 drafts)'}
                  </Button>
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => handleRefreshCurriculum(selectedTenant.id)}
                    disabled={seedingCurriculumTenantId === selectedTenant.id}
                    title="Re-apply the seed library to this tenant: every UNPUBLISHED draft from the library is replaced with the latest content. Published cases are preserved (never touched)."
                  >
                    {seedingCurriculumTenantId === selectedTenant.id ? 'Refreshing…' : 'Refresh curriculum drafts (overwrite unpublished)'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleSeedDrills(selectedTenant.id)}
                    disabled={seedingDrillsTenantId === selectedTenant.id}
                    title="Insert the curated calculation-exercise library (infusion rates + paediatric weight-based dosing). Drafts; idempotent on title."
                  >
                    {seedingDrillsTenantId === selectedTenant.id ? 'Seeding…' : 'Seed exercise library (16 drafts)'}
                  </Button>
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => handleRefreshDrills(selectedTenant.id)}
                    disabled={seedingDrillsTenantId === selectedTenant.id}
                    title="Re-apply the seed exercise library: every UNPUBLISHED exercise from the library is replaced with the latest content. Published exercises are preserved (never touched)."
                  >
                    {seedingDrillsTenantId === selectedTenant.id ? 'Refreshing…' : 'Refresh exercise drafts (overwrite unpublished)'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleSeedSpotError(selectedTenant.id)}
                    disabled={seedingSpotErrorTenantId === selectedTenant.id}
                    title="Insert the curated Spot-the-Error exercise library (10 timed scenarios). Drafts; idempotent on title."
                  >
                    {seedingSpotErrorTenantId === selectedTenant.id ? 'Seeding…' : 'Seed Spot-the-Error library (10 drafts)'}
                  </Button>
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => handleRefreshSpotError(selectedTenant.id)}
                    disabled={seedingSpotErrorTenantId === selectedTenant.id}
                    title="Re-apply the seed Spot-the-Error library: every UNPUBLISHED exercise is replaced with the latest content. Published exercises are preserved."
                  >
                    {seedingSpotErrorTenantId === selectedTenant.id ? 'Refreshing…' : 'Refresh Spot-the-Error drafts (overwrite unpublished)'}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  if ((view === 'users' || view === 'students') && selectedTenant) {
    const cameFromHome = view === 'students';
    const headerTitle = cameFromHome
      ? `Student accounts — ${selectedTenant.displayName}`
      : `Users — ${selectedTenant.displayName}`;
    const backOpts = cameFromHome
      ? { label: 'Back to admin home', onClick: () => setView('home') }
      : { label: 'Back to tenants', onClick: () => setView('list') };
    const isCentral = selectedTenant.tenantType === 'central_students';
    const totalLabel = `${users.length} user${users.length === 1 ? '' : 's'}`;
    const headerSubtitle = isCentral && payingCount !== null
      ? `${totalLabel} • ${payingCount} paying`
      : totalLabel;
    return (
      <div className="student-page">
        <div className="student-page__content">
          {renderHeader(headerTitle, headerSubtitle, backOpts)}
          <div className="app-panel p-4">
            {usersLoading ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
            ) : users.length === 0 ? (
              <p className="text-muted mb-0">No users in this tenant yet.</p>
            ) : (
              <Table responsive hover size="sm" className="facilitator-admin-table mb-0">
                <thead>
                  <tr>
                    <th>Display name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    {selectedTenant?.tenantType === 'central_students' ? <th>Subscription</th> : null}
                    <th>Cases</th>
                    <th>Sessions</th>
                    <th>Last login</th>
                    <th>Last accessed</th>
                    <th className="facilitator-admin-table__actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.displayName}</td>
                      <td className="text-muted">{u.email}</td>
                      <td>
                        {u.role === 'platform_admin' ? (
                          <Badge bg="dark">Platform admin</Badge>
                        ) : (
                          <Form.Select
                            size="sm"
                            value={u.role}
                            onChange={(e) => handleChangeRole(u.id, e.target.value, u.role)}
                            style={{ minWidth: 140 }}
                            aria-label={`Change role for ${u.displayName}`}
                          >
                            <option value="student">Student</option>
                            <option value="educator">Educator</option>
                            <option value="educator_admin">Educator admin</option>
                          </Form.Select>
                        )}
                      </td>
                      <td>
                        {u.accountStatus === 'active' ? (
                          <Badge bg="success">Active</Badge>
                        ) : u.accountStatus === 'suspended' ? (
                          <Badge bg="warning" text="dark">Suspended</Badge>
                        ) : (
                          <Badge bg="secondary">{u.accountStatus}</Badge>
                        )}
                      </td>
                      {selectedTenant?.tenantType === 'central_students' ? (
                        <td>
                          {(() => {
                            const status = u.subscriptionStatus || 'free';
                            if (status === 'active' || status === 'trialing') return <Badge bg="success">Paying</Badge>;
                            if (status === 'past_due') return <Badge bg="warning" text="dark">Past due</Badge>;
                            if (status === 'canceled') return <Badge bg="secondary">Cancelled</Badge>;
                            return <Badge bg="light" text="dark">Free</Badge>;
                          })()}
                        </td>
                      ) : null}
                      <td>{u.ownedCaseStudyCount}</td>
                      <td>{u.caseSessionCount}</td>
                      <td className="small text-muted">{u.lastLoginAt ? formatDate(u.lastLoginAt) : '—'}</td>
                      <td className="small text-muted">{u.lastActivityAt ? formatDate(u.lastActivityAt) : '—'}</td>
                      <td className="facilitator-admin-table__actions">
                        {u.accountStatus === 'suspended' ? (
                          <Button size="sm" variant="outline-success" onClick={() => handleRestoreUser(u.id)}>Restore</Button>
                        ) : (
                          <Button size="sm" variant="outline-dark" onClick={() => handleSuspendUser(u.id)}>Suspend</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="student-page">
      <div className="student-page__content">
        {renderHeader('Tenant management', 'Create, edit, suspend tenants and manage their users', {
          label: 'Back to admin home',
          onClick: () => setView('home'),
        })}
        {error ? <Alert variant="danger">{error}</Alert> : null}
        <div className="app-panel p-4">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h2 className="h5 mb-0">{tenants.length} tenant{tenants.length === 1 ? '' : 's'}</h2>
            <Button variant="primary" onClick={() => { setCreateDraft(EMPTY_DRAFT); setCreateError(''); setCreateModal(true); }}>
              + Create tenant
            </Button>
          </div>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
          ) : tenants.length === 0 ? (
            <p className="text-muted mb-0">No tenants yet.</p>
          ) : (
            <Table responsive hover className="facilitator-admin-table mb-0">
              <thead>
                <tr>
                  <th>Display name</th>
                  <th>Slug</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Users</th>
                  <th>Cases</th>
                  <th className="facilitator-admin-table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id}>
                    <td className="fw-semibold">{t.displayName}</td>
                    <td><code>{t.slug}</code></td>
                    <td>{t.tenantType === 'central_students' ? 'Central' : 'University'}</td>
                    <td>
                      {t.status === 'active' ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="warning" text="dark">Suspended</Badge>
                      )}
                    </td>
                    <td>{t.userCount}</td>
                    <td>{t.caseStudyCount}</td>
                    <td className="facilitator-admin-table__actions">
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(t)}>Edit</Button>{' '}
                      <Button size="sm" variant="outline-secondary" onClick={() => openUsers(t)}>Users</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal show={createModal} onHide={() => setCreateModal(false)}>
        <Form onSubmit={handleCreateSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Create tenant</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {createError ? <Alert variant="danger">{createError}</Alert> : null}
            <Form.Group className="mb-3">
              <Form.Label>Display name</Form.Label>
              <Form.Control
                value={createDraft.displayName}
                onChange={(e) => setCreateDraft({ ...createDraft, displayName: e.target.value })}
                placeholder="University of Oxford"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Slug (subdomain)</Form.Label>
              <Form.Control
                value={createDraft.slug}
                onChange={(e) => setCreateDraft({ ...createDraft, slug: e.target.value })}
                placeholder="oxford"
                required
              />
              <Form.Text className="text-muted">
                Will be reachable at <code>{createDraft.slug || 'oxford'}.{PUBLIC_DOMAIN}</code>.
                Lowercase letters, digits and hyphens only.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tenant type</Form.Label>
              <Form.Select
                value={createDraft.tenantType}
                onChange={(e) => setCreateDraft({ ...createDraft, tenantType: e.target.value })}
              >
                {TENANT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Contact email</Form.Label>
              <Form.Control
                type="email"
                value={createDraft.contactEmail}
                onChange={(e) => setCreateDraft({ ...createDraft, contactEmail: e.target.value })}
                placeholder="admin@oxford.ac.uk"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setCreateModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={createSaving}>
              {createSaving ? 'Creating…' : 'Create tenant'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default PlatformAdminWorkspace;
